/**
 * IMPORTS
 */
import { useRef, useState } from "react";

import { Paperclip, SendHorizontal, X } from "lucide-react";

import {
  EXTENSOES_ANEXO_PERMITIDAS,
  ehImagemAnexo,
  type IResponderConversaPayload,
  TAMANHO_MAXIMO_MENSAGEM,
} from "@/interface/conversa-cliente";
import { notifyError } from "@/utils/notify-messages";

import { formatarTamanhoArquivo, validarArquivosAnexo } from "../helpers/formatadores-conversa";
import { MiniaturaArquivoSelecionado } from "./miniatura-arquivo-selecionado";

type CompositorConversaProps = {
  enviando: boolean;
  onEnviar: (payload: IResponderConversaPayload) => Promise<unknown>;
};

const ACCEPT = EXTENSOES_ANEXO_PERMITIDAS.map((extensao) => `.${extensao}`).join(",");

function CompositorConversa({ enviando, onEnviar }: CompositorConversaProps) {
  const [conteudo, setConteudo] = useState("");
  const [anexos, setAnexos] = useState<File[]>([]);
  const inputArquivoRef = useRef<HTMLInputElement>(null);

  const podeEnviar = !enviando && (conteudo.trim().length > 0 || anexos.length > 0);

  const adicionarArquivos = (lista: FileList | null) => {
    const novos = Array.from(lista ?? []);
    if (novos.length === 0) return;

    const erro = validarArquivosAnexo(anexos, novos);
    if (erro) {
      notifyError(erro);
      return;
    }
    setAnexos((atuais) => [...atuais, ...novos]);
  };

  const enviar = async () => {
    if (!podeEnviar) return;
    try {
      await onEnviar({ conteudo, anexos });
      setConteudo("");
      setAnexos([]);
    } catch {
      // o erro já foi exibido pela tela; o rascunho fica para nova tentativa
    }
  };

  return (
    <form
      className="flex flex-col gap-2 border-t border-gray-200 bg-white p-3"
      onSubmit={(evento) => {
        evento.preventDefault();
        void enviar();
      }}
    >
      {anexos.length > 0 && (
        <ul className="flex flex-wrap gap-2" aria-label="Arquivos selecionados">
          {anexos.map((arquivo, indice) => {
            const ehImagem = ehImagemAnexo({ mimeType: arquivo.type, nome: arquivo.name });
            return (
              <li
                key={`${arquivo.name}-${indice}`}
                className={`flex max-w-60 items-center gap-1.5 border border-gray-200 bg-gray-50 py-1 pr-1 text-xs ${
                  ehImagem ? "rounded-lg pl-1" : "rounded-full pl-3"
                }`}
              >
                {ehImagem && <MiniaturaArquivoSelecionado arquivo={arquivo} />}
                <span className="truncate">{arquivo.name}</span>
                <span className="shrink-0 text-gray-500">
                  {formatarTamanhoArquivo(arquivo.size)}
                </span>
                <button
                  type="button"
                  aria-label={`Remover ${arquivo.name}`}
                  onClick={() => setAnexos((atuais) => atuais.filter((_, i) => i !== indice))}
                  className="rounded-full p-0.5 hover:bg-gray-200"
                >
                  <X className="size-3" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <label htmlFor="mensagem-conversa" className="sr-only">
        Mensagem
      </label>
      <textarea
        id="mensagem-conversa"
        placeholder="Escreva sua mensagem... (Ctrl+Enter para enviar)"
        value={conteudo}
        maxLength={TAMANHO_MAXIMO_MENSAGEM}
        onChange={(evento) => setConteudo(evento.target.value)}
        onKeyDown={(evento) => {
          if (evento.key === "Enter" && (evento.ctrlKey || evento.metaKey)) {
            evento.preventDefault();
            void enviar();
          }
        }}
        disabled={enviando}
        className="min-h-20 w-full resize-y rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 caret-gray-900 outline-none placeholder:text-gray-400 focus-visible:ring-2 focus-visible:ring-gray-custom900 disabled:opacity-60"
      />

      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={inputArquivoRef}
          type="file"
          multiple
          accept={ACCEPT}
          className="hidden"
          onChange={(evento) => {
            adicionarArquivos(evento.target.files);
            evento.target.value = "";
          }}
        />
        <button
          type="button"
          onClick={() => inputArquivoRef.current?.click()}
          disabled={enviando}
          className="flex h-9 items-center gap-1 rounded-md border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <Paperclip className="size-4" />
          Anexar
        </button>
        <span className="hidden text-xs text-gray-500 sm:inline">Até 5 arquivos de 10 MB</span>

        <button
          type="submit"
          disabled={!podeEnviar}
          className="ml-auto flex h-9 items-center gap-1 rounded-md bg-gray-custom900 px-4 text-sm font-medium text-white hover:bg-[#363740] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-custom900 focus-visible:ring-offset-2 disabled:opacity-50"
        >
          <SendHorizontal className="size-4" />
          {enviando ? "Enviando..." : "Enviar"}
        </button>
      </div>
    </form>
  );
}

/**
 * EXPORTS
 */
export { CompositorConversa };
